/**
 * SelectionGroupOverlay.tsx - Visual feedback for group drag operations
 *
 * Displays:
 * - Drag ghost showing the selection bounds during drag
 * - Drop zone indicators showing valid insertion points
 * - Branch drop zones for switch nodes
 *
 * Related:
 *   - hooks/useGroupDrag.ts - Drag state management
 *   - hooks/useSelectionGroup.ts - Selection group building
 */

import { css } from 'aphrodite';
import { Panel, useViewport } from '@xyflow/react';
import type { GroupDragState, GroupDropZone } from '../types/selectionGroup';
import { styleSheet as styles } from './styles/SelectionGroupOverlayStyles';

/**
 * Props for SelectionGroupOverlay
 */
export interface SelectionGroupOverlayProps {
	/** Current drag state from useGroupDrag */
	dragState: GroupDragState;
}

/**
 * Type guard to check if drop zone type is valid for rendering
 */
function isValidDropType(dropZone: GroupDropZone): dropZone is GroupDropZone & { type: 'after' | 'before' | 'branch' } {
	return dropZone.type === 'after' || dropZone.type === 'before' || dropZone.type === 'branch';
}

/**
 * Visual overlay component for group drag operations.
 *
 * Shows a ghost of the selection bounds during drag and indicates
 * valid drop zones with visual feedback.
 *
 * @example
 * ```tsx
 * <ReactFlow ...>
 *   <SelectionGroupOverlay dragState={dragState} nodes={nodes} />
 * </ReactFlow>
 * ```
 */
export function SelectionGroupOverlay({ dragState }: SelectionGroupOverlayProps): JSX.Element | null {
	const { x, y, zoom } = useViewport();

	// Don't render if not dragging or no group
	if (!dragState.isDragging || !dragState.group) {
		return null;
	}

	const { group, dropZone, mousePosition, dragOffset } = dragState;

	// Calculate ghost position based on mouse position and offset
	const ghostX = mousePosition ? mousePosition.x - dragOffset.x : group.bounds.x;
	const ghostY = mousePosition ? mousePosition.y - dragOffset.y : group.bounds.y;

	const isValidDrop = dropZone && dropZone.type !== 'invalid';

	return (
		<Panel position='top-left' className={css(styles.panelOverlay)}>
			{/* Transform container to match React Flow's viewport */}
			<div
				className={css(styles.transformContainer)}
				style={{
					transform: `translate(${x}px, ${y}px) scale(${zoom})`,
					transformOrigin: '0 0',
				}}
			>
				{/* Drag Ghost - semi-transparent bounding box */}
				<div
					className={css(styles.dragGhost, isValidDrop && styles.dragGhostValid)}
					style={{
						left: ghostX,
						top: ghostY,
						width: group.bounds.width,
						height: group.bounds.height,
					}}
				>
					<span className={css(styles.ghostLabel)}>
						{group.nodeIds.length} node{group.nodeIds.length > 1 ? 's' : ''}
					</span>
				</div>

				{/* Drop Zone Indicator */}
				{dropZone && isValidDropType(dropZone) && dropZone.indicatorPosition && (
					<DropZoneIndicator
						type={dropZone.type}
						position={dropZone.indicatorPosition}
						branchInfo={dropZone.branchInfo}
					/>
				)}
			</div>
		</Panel>
	);
}

/**
 * Props for DropZoneIndicator
 */
interface DropZoneIndicatorProps {
	type: 'after' | 'before' | 'branch';
	position: { x: number; y: number };
	branchInfo?: { switchNodeId: string; caseIndex: number };
}

/**
 * Indicator showing where the group will be dropped
 */
function DropZoneIndicator({ type, position, branchInfo }: DropZoneIndicatorProps): JSX.Element {
	const isBranch = type === 'branch';

	return (
		<div
			className={css(styles.dropZoneIndicator, isBranch && styles.dropZoneBranch)}
			style={{
				left: position.x - 100, // Center the 200px wide indicator
				top: position.y - 2,
			}}
		>
			<div className={css(styles.dropZoneLine)} />
			<div className={css(styles.dropZoneCircle)}>
				{isBranch && branchInfo && <span className={css(styles.branchLabel)}>Branch {branchInfo.caseIndex + 1}</span>}
			</div>
			<div className={css(styles.dropZoneLine)} />
		</div>
	);
}

/**
 * Styles for SelectionGroupOverlay
 */
